import { loadAppSetting, resetAppSetting, saveAppSetting } from '$lib/app-settings.js';

export const CALL_TOOLBAR_SETTING_KEY = 'messenger.call-toolbar';

export const CALL_TOOLBAR_ACTIONS = [
  { id: 'microphone', label: 'Microphone', description: 'Bật hoặc tắt tiếng của bạn', modes: ['audio', 'video'] },
  { id: 'camera', label: 'Camera', description: 'Bật hoặc tắt camera', modes: ['video'] },
  { id: 'shareScreen', label: 'Chia sẻ màn hình', description: 'Trình bày cửa sổ hoặc màn hình', modes: ['video'] },
  { id: 'layout', label: 'Bố cục video', description: 'Chuyển sang bố cục chia đôi', modes: ['video'] },
  { id: 'settings', label: 'Cài đặt', description: 'Thiết bị, mạng và hiển thị', modes: ['audio', 'video'], locked: true },
  { id: 'endCall', label: 'Kết thúc', description: 'Kết thúc cuộc gọi hiện tại', modes: ['audio', 'video'], locked: true },
  { id: 'minimize', label: 'Thu nhỏ', description: 'Đưa cuộc gọi về cửa sổ nhỏ', modes: ['audio', 'video'], desktopOnly: true }
];

export const CALL_TOOLBAR_PRESETS = [
  {
    id: 'balanced',
    label: 'Cân bằng',
    description: 'Đầy đủ công cụ thường dùng',
    style: 'labels',
    visible: ['microphone', 'camera', 'shareScreen', 'layout', 'settings', 'endCall', 'minimize']
  },
  {
    id: 'focus',
    label: 'Tập trung',
    description: 'Giữ lại các thao tác thiết yếu',
    style: 'compact',
    visible: ['microphone', 'camera', 'settings', 'endCall']
  },
  {
    id: 'presenter',
    label: 'Trình bày',
    description: 'Ưu tiên chia sẻ và điều khiển nhanh',
    style: 'glass',
    visible: ['microphone', 'camera', 'shareScreen', 'settings', 'endCall', 'minimize']
  }
];

export const DEFAULT_CALL_TOOLBAR_CONFIG = {
  preset: 'balanced',
  style: 'labels',
  actions: CALL_TOOLBAR_ACTIONS.map(({ id }) => ({ id, visible: true }))
};

export function normalizeCallToolbarConfig(value = {}) {
  const incoming = Array.isArray(value.actions) ? value.actions : [];
  const byId = new Map(incoming.map((item) => [item.id, item]));
  const orderedIds = incoming.map(({ id }) => id).filter((id) => CALL_TOOLBAR_ACTIONS.some((action) => action.id === id));
  const missingIds = CALL_TOOLBAR_ACTIONS.map(({ id }) => id).filter((id) => !orderedIds.includes(id));

  return {
    preset: value.preset === 'custom' || CALL_TOOLBAR_PRESETS.some(({ id }) => id === value.preset) ? value.preset : 'balanced',
    style: ['labels', 'compact', 'glass'].includes(value.style) ? value.style : 'labels',
    actions: [...orderedIds, ...missingIds].map((id) => {
      const definition = CALL_TOOLBAR_ACTIONS.find((action) => action.id === id);
      return {
        id,
        visible: definition?.locked ? true : byId.get(id)?.visible !== false
      };
    })
  };
}

export function isCallToolbarConfigEqual(left, right) {
  return JSON.stringify(normalizeCallToolbarConfig(left)) === JSON.stringify(normalizeCallToolbarConfig(right));
}

export function applyCallToolbarPreset(config, presetId) {
  const preset = CALL_TOOLBAR_PRESETS.find(({ id }) => id === presetId) || CALL_TOOLBAR_PRESETS[0];
  const visible = new Set(preset.visible);

  return normalizeCallToolbarConfig({
    ...config,
    preset: preset.id,
    style: preset.style,
    actions: config.actions.map((action) => ({
      ...action,
      visible: visible.has(action.id)
    }))
  });
}

export async function loadCallToolbarConfig() {
  const response = await loadAppSetting(CALL_TOOLBAR_SETTING_KEY);
  return normalizeCallToolbarConfig(response?.effective);
}

export async function saveCallToolbarConfig(config) {
  const normalized = normalizeCallToolbarConfig(config);
  await saveAppSetting(CALL_TOOLBAR_SETTING_KEY, normalized);
  return normalized;
}

export async function resetCallToolbarConfig() {
  await resetAppSetting(CALL_TOOLBAR_SETTING_KEY);
  return loadCallToolbarConfig();
}
